import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'calories', 'goal', 'weight', 'weightUnit',
    'proteinGrams', 'proteinCals', 'proteinPercent', 'proteinBar', 'proteinPerKg',
    'carbsGrams', 'carbsCals', 'carbsPercent', 'carbsBar',
    'fatGrams', 'fatCals', 'fatPercent', 'fatBar',
    'summary'
  ]

  // Macro splits by goal: { protein%, carbs%, fat% }
  macroSplits = {
    'balanced': { protein: 30, carbs: 40, fat: 30 },
    'low-carb': { protein: 35, carbs: 25, fat: 40 },
    'high-protein': { protein: 40, carbs: 35, fat: 25 },
    'keto': { protein: 25, carbs: 5, fat: 70 }
  }

  connect() {
    this.calculate()
  }

  calculate() {
    const calories = parseFloat(this.caloriesTarget.value) || 2000
    const goal = this.goalTarget.value
    let weight = parseFloat(this.weightTarget.value) || 70
    const weightUnit = this.weightUnitTarget.value

    // Convert to kg if needed
    if (weightUnit === 'lbs') {
      weight /= 2.205
    }

    // Get macro percentages for selected goal
    const split = this.macroSplits[goal] || this.macroSplits.balanced

    // Calculate calories from each macro
    const proteinCals = Math.round(calories * (split.protein / 100))
    const carbsCals = Math.round(calories * (split.carbs / 100))
    const fatCals = Math.round(calories * (split.fat / 100))

    // Calculate grams (protein/carbs = 4 cal/g, fat = 9 cal/g)
    const proteinGrams = Math.round(proteinCals / 4)
    const carbsGrams = Math.round(carbsCals / 4)
    const fatGrams = Math.round(fatCals / 9)

    // Calculate protein per kg
    const proteinPerKg = (proteinGrams / weight).toFixed(1)

    // Update protein display
    this.proteinGramsTarget.textContent = proteinGrams
    this.proteinCalsTarget.textContent = proteinCals
    this.proteinPercentTarget.textContent = `${split.protein}%`
    this.proteinBarTarget.style.width = `${split.protein}%`
    this.proteinPerKgTarget.textContent = proteinPerKg

    // Update carbs display
    this.carbsGramsTarget.textContent = carbsGrams
    this.carbsCalsTarget.textContent = carbsCals
    this.carbsPercentTarget.textContent = `${split.carbs}%`
    this.carbsBarTarget.style.width = `${split.carbs}%`

    // Update fat display
    this.fatGramsTarget.textContent = fatGrams
    this.fatCalsTarget.textContent = fatCals
    this.fatPercentTarget.textContent = `${split.fat}%`
    this.fatBarTarget.style.width = `${split.fat}%`

    // Update summary
    this.summaryTarget.textContent = this.getSummary(goal, proteinPerKg)
  }

  getSummary(goal, proteinPerKg) {
    const summaries = {
      'balanced': `A balanced macro split provides adequate protein (${proteinPerKg}g/kg) for muscle maintenance, ` +
        'sufficient carbs for energy, and healthy fats for hormonal function. This is sustainable long-term.',
      'low-carb': `A low-carb approach with ${proteinPerKg}g/kg protein helps control blood sugar and may ` +
        'improve fat loss. Ensure adequate fiber from vegetables and consider timing carbs around workouts.',
      'high-protein': `High protein at ${proteinPerKg}g/kg maximizes muscle protein synthesis and satiety. ` +
        'Ideal for building muscle or preserving it during fat loss. Space protein intake across 4-5 meals.',
      'keto': `Ketogenic macros with very low carbs (under 50g) push your body into ketosis. Protein at ` +
        `${proteinPerKg}g/kg preserves muscle while fat becomes your primary fuel. Monitor electrolytes closely.`
    }
    return summaries[goal] || summaries.balanced
  }
}
